/**
 * EvaluationCriteriaItemModals - Modal Management for Evaluation Criteria
 *
 * This module provides a React Context-based solution for managing modals
 * related to evaluation criteria and their items (behaviors).
 *
 * ## Why This Pattern?
 *
 * 1. **Modal State Isolation**: Each evaluation criterion needs its own set of
 *    modals (EditLabel, AddItem, EditItem, DeleteItem, DeleteCriterion).
 *    This pattern isolates modal state per criterion, preventing conflicts.
 *
 * 2. **Deep Component Access**: Child components like menu items and behavior
 *    cards need to trigger modals without prop drilling through multiple levels.
 *
 * 3. **Consistent Mutation Handling**: All GraphQL mutations follow the same
 *    pattern with loading states, error handling, and data refetching.
 *
 * 4. **Type Safety**: Full TypeScript support ensures compile-time safety for
 *    all modal operations.
 *
 * ## Architecture Overview
 *
 * ```
 * EvaluationCriteriaItemModalsProvider (holds state & renders modals)
 *     ├── EvaluationCriteriaCard
 *     │   ├── Menu → useContext → openEditLabel(), openDeleteCriterion()
 *     │   └── BehaviorItems → useContext → openEditItem(), openDeleteItem()
 *     └── All Modal Components (rendered at provider level)
 * ```
 *
 * ## Usage Example
 *
 * ```tsx
 * // Wrap your criterion component
 * <EvaluationCriteriaItemModalsProvider criterion={criterion} refetch={refetch}>
 *   <EvaluationCriteriaCard criterion={criterion} />
 * </EvaluationCriteriaItemModalsProvider>
 *
 * // In any child component
 * const { openEditItem } = useEvaluationCriteriaItemModalsContext();
 * <Button onClick={() => openEditItem(itemId)}>Edit</Button>
 * ```
 */

import React, { createContext, useCallback, useContext, useState } from "react";

import { ConfirmDialog } from "@/components/pieces/dialog/ConfirmDialog";
import {
  EvaluationCriterionFieldsFragment,
  useAddEvaluationCriterionItemMutation,
  useDeleteEvaluationCriterionItemMutation,
  useDeleteEvaluationCriterionMutation,
  useReorderItemsWithinCriterionMutation,
  useUnlinkEvaluationCriterionItemMutation,
  useUnlinkEvaluationCriterionMutation,
  useUpdateEvaluationCriterionItemMutation,
  useUpdateEvaluationCriterionMutation,
} from "@/graphql";
import { EvaluationCriterionItem } from "@/types/roleplays";
import { formatErrors } from "@/utils/array";

import { AddBehaviorItemDialog } from "./dialogs/AddBehaviorItemDialog";
import { DeleteBehaviorItemDialog } from "./dialogs/DeleteBehaviorItemDialog";
import { EditCriterionLabelDialog } from "./dialogs/EditCriterionLabelDialog";
import { LinkedScenariosDialog } from "./dialogs/LinkedScenariosDialog";
import { UpdateBehaviorItemDialog } from "./dialogs/UpdateBehaviorItemDialog";

type ItemModal =
  | "none"
  | "edit-label"
  | "add-item"
  | "edit-item"
  | "delete-item"
  | "delete-criterion"
  | "unlink-criterion"
  | "unlink-item"
  | "linked-scenarios"
  | "linked-scenarios-item";

// ============================================================================
// Context Types
// ============================================================================

interface EvaluationCriteriaItemModalsContextValue {
  // For criterion-level actions
  openEditLabel: () => void;
  openAddItem: () => void;
  openDeleteCriterion: () => void;
  openUnlinkCriterion: () => void;
  openLinkedScenarios: () => void;

  // For item-level actions
  openEditItem: (itemId: string) => void;
  openDeleteItem: (itemId: string) => void;
  openUnlinkItem: (itemId: string) => void;
  openLinkedScenariosForItem: (itemId: string) => void;

  // Non-modal actions
  handleReorderItems: (reorderedItems: { id: string; order: number }[]) => void;
}

// ============================================================================
// Context Creation
// ============================================================================

const EvaluationCriteriaItemModalsContext =
  createContext<EvaluationCriteriaItemModalsContextValue | null>(null);

// ============================================================================
// Custom Hook - Core Modal Logic
// ============================================================================

interface UseEvaluationCriteriaItemModalsProps {
  criterion: EvaluationCriterionFieldsFragment;
  refetch: () => void;
}

/**
 * Custom hook that encapsulates all modal management logic.
 * This includes state management, GraphQL mutations, and action handlers.
 */
function useEvaluationCriteriaItemModals({
  criterion,
  refetch,
}: UseEvaluationCriteriaItemModalsProps) {
  // Modal state management
  const [activeModal, setActiveModal] = useState<ItemModal>("none");
  const [selectedItem, setSelectedItem] = useState<EvaluationCriterionItem>();
  const [errMsg, setErrMsg] = useState("");

  // GraphQL mutations - all mutations are handled here for consistency
  const [updateCriterion, { loading: updateNameLoading }] =
    useUpdateEvaluationCriterionMutation();
  const [addCriterionItem, { loading: addItemLoading }] =
    useAddEvaluationCriterionItemMutation();
  const [updateCriterionItem, { loading: updateItemLoading }] =
    useUpdateEvaluationCriterionItemMutation();
  const [deleteCriterionItem, { loading: deleteItemLoading }] =
    useDeleteEvaluationCriterionItemMutation();
  const [reorderItems] = useReorderItemsWithinCriterionMutation();
  const [deleteCriterion, { loading: deleteCriterionLoading }] =
    useDeleteEvaluationCriterionMutation();
  const [unlinkCriterion, { loading: unlinkCriterionLoading }] =
    useUnlinkEvaluationCriterionMutation();
  const [unlinkItem, { loading: unlinkItemLoading }] =
    useUnlinkEvaluationCriterionItemMutation();

  // Aggregate loading state for UI feedback
  const loading =
    updateNameLoading ||
    addItemLoading ||
    updateItemLoading ||
    deleteItemLoading ||
    deleteCriterionLoading ||
    unlinkCriterionLoading ||
    unlinkItemLoading;

  // ============================================================================
  // Modal Control Functions
  // ============================================================================

  /**
   * Closes the active modal and resets associated state
   */
  const closeModal = useCallback(() => {
    setActiveModal("none");
    setSelectedItem(undefined);
    setErrMsg("");
  }, []);

  // ============================================================================
  // Modal Opening Functions
  // ============================================================================

  const openEditLabel = useCallback(() => {
    setActiveModal("edit-label");
    setErrMsg("");
  }, []);

  const openAddItem = useCallback(() => {
    setActiveModal("add-item");
    setErrMsg("");
  }, []);

  /**
   * Opens the edit modal for a specific item.
   * Finds the item in the criterion's items array before opening.
   */
  const openEditItem = useCallback(
    (itemId: string) => {
      const item = criterion.items?.find((i) => i?.id === itemId);
      if (item) {
        setSelectedItem(item);
        setActiveModal("edit-item");
        setErrMsg("");
      }
    },
    [criterion],
  );

  /**
   * Opens the delete confirmation modal for a specific item.
   */
  const openDeleteItem = useCallback(
    (itemId: string) => {
      const item = criterion.items?.find((i) => i?.id === itemId);
      if (item) {
        setSelectedItem(item);
        setActiveModal("delete-item");
        setErrMsg("");
      }
    },
    [criterion],
  );

  const openDeleteCriterion = useCallback(() => {
    setActiveModal("delete-criterion");
    setErrMsg("");
  }, []);

  const openUnlinkCriterion = useCallback(() => {
    setActiveModal("unlink-criterion");
    setErrMsg("");
  }, []);

  const openUnlinkItem = useCallback(
    (itemId: string) => {
      const item = criterion.items?.find((i) => i?.id === itemId);
      if (item) {
        setSelectedItem(item);
        setActiveModal("unlink-item");
        setErrMsg("");
      }
    },
    [criterion],
  );

  const openLinkedScenarios = useCallback(() => {
    setActiveModal("linked-scenarios");
    setErrMsg("");
  }, []);

  const openLinkedScenariosForItem = useCallback(
    (itemId: string) => {
      const item = criterion.items?.find((i) => i?.id === itemId);
      if (item) {
        setSelectedItem(item);
        setActiveModal("linked-scenarios-item");
        setErrMsg("");
      }
    },
    [criterion],
  );

  // ============================================================================
  // Mutation Handlers - Consistent pattern for all operations
  // ============================================================================

  /**
   * Updates the criterion's label/name.
   * On success: closes modal and refetches data
   * On error: displays error message in modal
   */
  const handleUpdateLabel = useCallback(
    (criterionId: string, newName: string) => {
      updateCriterion({
        variables: {
          id: criterionId,
          name: newName,
        },
        onCompleted: ({ updateEvaluationCriterion }) => {
          if (updateEvaluationCriterion?.ok) {
            closeModal();
            refetch();
          } else {
            const errors = formatErrors(updateEvaluationCriterion?.errors);
            setErrMsg(errors);
          }
        },
        onError: () => {
          setErrMsg("Failed to update label");
        },
      });
    },
    [updateCriterion, closeModal, refetch],
  );

  /**
   * Adds a new item to the criterion.
   */
  const handleAddItem = useCallback(
    (criterionId: string, item: { name: string; description: string }) => {
      addCriterionItem({
        variables: {
          criterionId,
          name: item.name,
          description: item.description,
        },
        onCompleted: ({ addEvaluationCriterionItem }) => {
          if (addEvaluationCriterionItem?.ok) {
            closeModal();
            refetch();
          } else {
            const errors = formatErrors(addEvaluationCriterionItem?.errors);
            setErrMsg(errors);
          }
        },
        onError: () => {
          setErrMsg("Failed to add item");
        },
      });
    },
    [addCriterionItem, closeModal, refetch],
  );

  /**
   * Updates an existing item's name and description.
   */
  const handleUpdateItem = useCallback(
    (
      itemId: string,
      item: {
        name?: string;
        description?: string;
        detailedInstructions?: string;
      },
    ) => {
      updateCriterionItem({
        variables: {
          id: itemId,
          name: item.name,
          description: item.description,
          detailedInstructions: item.detailedInstructions,
        },
        onCompleted: ({ updateEvaluationCriterionItem }) => {
          if (updateEvaluationCriterionItem?.ok) {
            closeModal();
            refetch();
          } else {
            const errors = formatErrors(updateEvaluationCriterionItem?.errors);
            setErrMsg(errors);
          }
        },
        onError: () => {
          setErrMsg("Failed to update item");
        },
      });
    },
    [updateCriterionItem, closeModal, refetch],
  );

  /**
   * Deletes an item from the criterion.
   */
  const handleDeleteItem = useCallback(
    (itemId: string) => {
      deleteCriterionItem({
        variables: {
          id: itemId,
        },
        onCompleted: ({ deleteEvaluationCriterionItem }) => {
          if (deleteEvaluationCriterionItem?.ok) {
            closeModal();
            refetch();
          } else {
            const errors = formatErrors(deleteEvaluationCriterionItem?.errors);
            setErrMsg(errors);
          }
        },
        onError: () => {
          setErrMsg("Failed to delete item");
        },
      });
    },
    [deleteCriterionItem, closeModal, refetch],
  );

  /**
   * Deletes the entire criterion.
   */
  const handleDeleteCriterion = useCallback(() => {
    deleteCriterion({
      variables: {
        id: criterion.id,
      },
      onCompleted: ({ deleteEvaluationCriterion }) => {
        if (deleteEvaluationCriterion?.ok) {
          closeModal();
          refetch();
        } else {
          const errors = formatErrors(deleteEvaluationCriterion?.errors);
          setErrMsg(errors);
        }
      },
      onError: () => {
        setErrMsg("Failed to delete criterion");
      },
    });
  }, [deleteCriterion, criterion.id, closeModal, refetch]);

  const handleUnlinkCriterion = useCallback(() => {
    unlinkCriterion({
      variables: {
        id: criterion.id,
      },
      onCompleted: (data) => {
        if (data?.unlinkEvaluationCriterion?.evaluationCriterion) {
          closeModal();
          refetch();
        } else {
          const errors = data?.unlinkEvaluationCriterion?.errors || [];
          setErrMsg(
            errors.map((e) => e?.messages?.join(", ")).join("; ") ||
              "Failed to unlink criterion",
          );
        }
      },
      onError: () => {
        setErrMsg("Failed to unlink criterion");
      },
    });
  }, [unlinkCriterion, criterion.id, closeModal, refetch]);

  const handleUnlinkItem = useCallback(
    (itemId: string) => {
      unlinkItem({
        variables: {
          id: itemId,
        },
        onCompleted: (data) => {
          if (data?.unlinkEvaluationCriterionItem?.evaluationCriterionItem) {
            closeModal();
            refetch();
          } else {
            const errors = data?.unlinkEvaluationCriterionItem?.errors || [];
            setErrMsg(
              errors.map((e) => e?.messages?.join(", ")).join("; ") ||
                "Failed to unlink item",
            );
          }
        },
        onError: () => {
          setErrMsg("Failed to unlink item");
        },
      });
    },
    [unlinkItem, closeModal, refetch],
  );

  /**
   * Reorders items within the criterion.
   * This is a non-modal action that happens inline with drag-and-drop.
   * On failure, refetches to restore the correct order.
   */
  const handleReorderItems = useCallback(
    (reorderedItems: { id: string; order: number }[]) => {
      reorderItems({
        variables: {
          criterionId: criterion.id,
          items: reorderedItems,
        },
        onCompleted: ({ reorderEvaluationCriterionItems }) => {
          if (!reorderEvaluationCriterionItems?.ok) {
            console.error("Failed to reorder items");
            // On failure, refetch to restore correct order
            refetch();
          }
          // On success, don't refetch - let local state handle it
        },
        onError: (error) => {
          console.error("Failed to reorder items:", error);
          // On error, refetch to restore correct order
          refetch();
        },
      });
    },
    [reorderItems, refetch, criterion],
  );

  return {
    // State
    activeModal,
    selectedItem,
    errMsg,
    loading,

    // Modal control
    closeModal,

    // Open handlers
    openEditLabel,
    openAddItem,
    openEditItem,
    openDeleteItem,
    openDeleteCriterion,
    openUnlinkCriterion,
    openUnlinkItem,
    openLinkedScenarios,
    openLinkedScenariosForItem,

    // Save handlers
    handleUpdateLabel,
    handleAddItem,
    handleUpdateItem,
    handleDeleteItem,
    handleDeleteCriterion,
    handleUnlinkCriterion,
    handleUnlinkItem,

    // Non-modal actions
    handleReorderItems,
  };
}

// ============================================================================
// Provider Component
// ============================================================================

interface EvaluationCriteriaItemModalsProviderProps {
  criterion: EvaluationCriterionFieldsFragment;
  refetch: () => void;
  children: React.ReactNode;
}

/**
 * Provider component that manages modals for a single evaluation criterion.
 *
 * This component:
 * 1. Creates the modal management state using the custom hook
 * 2. Provides context values to child components
 * 3. Renders all modal components at this level (preventing duplication)
 *
 * Each criterion gets its own provider instance, ensuring state isolation.
 */
export function EvaluationCriteriaItemModalsProvider(
  props: EvaluationCriteriaItemModalsProviderProps,
) {
  const {
    activeModal,
    selectedItem,
    errMsg,
    loading,
    closeModal,
    openEditLabel,
    openAddItem,
    openEditItem,
    openDeleteItem,
    openDeleteCriterion,
    openUnlinkCriterion,
    openUnlinkItem,
    openLinkedScenarios,
    openLinkedScenariosForItem,
    handleUpdateLabel,
    handleAddItem,
    handleUpdateItem,
    handleDeleteItem,
    handleDeleteCriterion,
    handleUnlinkCriterion,
    handleUnlinkItem,
    handleReorderItems,
  } = useEvaluationCriteriaItemModals({
    criterion: props.criterion,
    refetch: props.refetch,
  });

  // Context value only includes functions that child components need
  const contextValue: EvaluationCriteriaItemModalsContextValue = {
    openEditLabel,
    openAddItem,
    openEditItem,
    openDeleteItem,
    openDeleteCriterion,
    openUnlinkCriterion,
    openUnlinkItem,
    openLinkedScenarios,
    openLinkedScenariosForItem,
    handleReorderItems,
  };

  return (
    <EvaluationCriteriaItemModalsContext.Provider value={contextValue}>
      {/* Render children first */}
      {props.children}

      {/* All modals are rendered here at the provider level */}
      <EditCriterionLabelDialog
        open={activeModal === "edit-label"}
        onClose={closeModal}
        criterion={props.criterion}
        onSave={handleUpdateLabel}
        loading={loading}
        errorMessage={errMsg}
      />

      <AddBehaviorItemDialog
        open={activeModal === "add-item"}
        onClose={closeModal}
        criterion={props.criterion}
        onSave={handleAddItem}
        loading={loading}
        errorMessage={errMsg}
      />

      {selectedItem && (
        <UpdateBehaviorItemDialog
          open={activeModal === "edit-item"}
          onClose={closeModal}
          item={selectedItem}
          onSave={handleUpdateItem}
          loading={loading}
          errorMessage={errMsg}
        />
      )}

      {selectedItem && (
        <DeleteBehaviorItemDialog
          open={activeModal === "delete-item"}
          onClose={closeModal}
          item={selectedItem}
          onDelete={handleDeleteItem}
          loading={loading}
          errorMessage={errMsg}
        />
      )}

      {/* Simple confirmation dialog for criterion deletion */}
      {activeModal === "delete-criterion" && (
        <ConfirmDialog
          onCancel={closeModal}
          onConfirm={handleDeleteCriterion}
          confirmLabel="Remove"
          intent="danger"
          cancelLabel="Nevermind"
        >
          <p>Are you sure you want to remove this criterion?</p>
        </ConfirmDialog>
      )}

      {/* Confirmation dialog for unlinking criterion */}
      {activeModal === "unlink-criterion" && (
        <ConfirmDialog
          onCancel={closeModal}
          onConfirm={handleUnlinkCriterion}
          confirmLabel="Unlink"
          cancelLabel="Cancel"
        >
          <p>
            This will permanently disconnect this criterion from the rubric.
            You'll be able to edit all fields, but won't receive future updates.
            Continue?
          </p>
        </ConfirmDialog>
      )}

      {/* Confirmation dialog for unlinking item */}
      {activeModal === "unlink-item" && selectedItem && (
        <ConfirmDialog
          onCancel={closeModal}
          onConfirm={() => handleUnlinkItem(selectedItem.id)}
          confirmLabel="Unlink"
          cancelLabel="Cancel"
        >
          <p>
            This will permanently disconnect "{selectedItem.name}" from the
            rubric. You'll be able to edit all fields, but won't receive future
            updates. Continue?
          </p>
        </ConfirmDialog>
      )}

      {/* Linked scenarios dialog for criterion */}
      <LinkedScenariosDialog
        open={activeModal === "linked-scenarios"}
        onClose={closeModal}
        criterion={props.criterion}
      />

      {/* Linked scenarios dialog for item */}
      <LinkedScenariosDialog
        open={activeModal === "linked-scenarios-item"}
        onClose={closeModal}
        item={selectedItem}
      />
    </EvaluationCriteriaItemModalsContext.Provider>
  );
}

// ============================================================================
// Context Consumer Hook
// ============================================================================

/**
 * Hook to access modal controls from any child component.
 * Throws an error if used outside of the provider to catch mistakes early.
 */
export function useEvaluationCriteriaItemModalsContext() {
  const context = useContext(EvaluationCriteriaItemModalsContext);
  if (!context) {
    throw new Error(
      "useEvaluationCriteriaItemModalsContext must be used within EvaluationCriteriaItemModalsProvider",
    );
  }
  return context;
}
